import { Select } from '@base-ui/react/select';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import isFunction from 'lodash/isFunction';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { FormGroup, Label } from 'reactstrap';

import { blue } from '../../../app/colors';
import { useThemeContext } from '../../../app/theme-provider';
import SingleErrorDisplay from '../../error-display/single-error-display';
import { useFormFieldContext } from '../../form-hook';
import { Tooltip } from '../../tooltip';
import styles from '../select-field.module.scss';

import Sort from './sort';

export interface SelectFieldOptionProp {
  value: string | number | boolean;
  label: string;
}

export const getOptionValues = (options: SelectFieldOptionProp[]) =>
  options.map((m) => m.value.toString());

interface SelectFieldProps {
  label: string;
  name: string;
  options: SelectFieldOptionProp[];
  required?: boolean;
  information?: string;
  onChange?: (v?: any) => void;
  placeholder?: string;
}

interface BaseSelectProps {
  label: string;
  name: string;
  placeholder: string;
  theme: string;
  options: SelectFieldOptionProp[];
  fieldValue: any;
  fieldOnChange: (value: any) => void;
  fieldError: any;
  onChange?: (v?: any) => void;
}

function BaseSelect({
  label,
  name,
  placeholder,
  theme,
  options,
  fieldValue,
  fieldOnChange,
  fieldError,
  onChange,
}: BaseSelectProps) {
  const sortedOptions = useMemo(() => Sort(options), [options]);

  const optionMap = useMemo(() => {
    const map = new Map<string, SelectFieldOptionProp>();
    for (const opt of sortedOptions) {
      map.set(String(opt.value), opt);
    }
    return map;
  }, [sortedOptions]);

  const selectedOption = sortedOptions.find((opt) => opt.value === fieldValue);
  const selectedKey = selectedOption ? String(selectedOption.value) : '';

  return (
    <div className="mb-2">
      <Select.Root
        items={sortedOptions}
        value={selectedKey}
        onValueChange={(key) => {
          if (!key) return;

          const option = optionMap.get(key);
          if (option) {
            fieldOnChange(option.value);
            if (isFunction(onChange)) onChange(option);
          }
        }}
      >
        <Select.Trigger
          className={cx(styles.selectTrigger, {
            [styles.selectTriggerDark]: theme === 'dark',
            [styles.selectTriggerError]: fieldError,
          })}
          aria-label={label}
          data-testid={`${name}-trigger`}
        >
          <span className={styles.selectValue}>
            {selectedOption?.label || placeholder}
          </span>
          <Select.Icon className={styles.selectIcon}>
            <FontAwesomeIcon icon={faChevronDown} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Positioner
            className={styles.selectPositioner}
            sideOffset={5}
            alignItemWithTrigger={false}
          >
            <Select.Popup
              className={cx(styles.selectPopup, {
                [styles.selectPopupDark]: theme === 'dark',
              })}
            >
              <Select.ScrollUpArrow className={styles.scrollArrow}>
                ▲
              </Select.ScrollUpArrow>
              <Select.List className={styles.selectList}>
                {sortedOptions.map((option) => {
                  const key = String(option.value);
                  return (
                    <Select.Item
                      key={key}
                      value={key}
                      className={cx(styles.selectItem, {
                        [styles.selectItemDark]: theme === 'dark',
                      })}
                    >
                      <Select.ItemIndicator
                        className={styles.selectItemIndicator}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </Select.ItemIndicator>
                      <Select.ItemText className={styles.selectItemText}>
                        {option.label}
                      </Select.ItemText>
                    </Select.Item>
                  );
                })}
              </Select.List>
              <Select.ScrollDownArrow className={styles.scrollArrow}>
                ▼
              </Select.ScrollDownArrow>
            </Select.Popup>
          </Select.Positioner>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}

export default function SelectField({
  label,
  name,
  options,
  required,
  onChange,
  information,
  placeholder = '',
}: SelectFieldProps) {
  const { theme } = useThemeContext();
  const { control } = useFormFieldContext(name);

  return (
    <FormGroup>
      {label && (
        <div className="d-flex align-items-center">
          <Label
            name={label || name}
            data-testid={name}
            className={cx('me-2', { required })}
          >
            {label || name}
          </Label>
          {information && (
            <Tooltip tooltip={information}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="lg"
                color={theme === 'dark' ? '#52baff' : blue}
                tabIndex={0}
              />
            </Tooltip>
          )}
        </div>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <BaseSelect
            label={label}
            name={name}
            placeholder={placeholder}
            theme={theme}
            options={options}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
            fieldError={fieldState.error}
            onChange={onChange}
          />
        )}
      />
      <SingleErrorDisplay field={name} />
    </FormGroup>
  );
}
